import { TranslationService } from '../services/translation.service'
import { ProductCsvTemplate } from '../types/csvTemplates'

export type ProductCsvMappingField = {
  name: keyof ProductCsvTemplate
  label: string
}

/**
 * Returns the shared mapping keys and translated labels for the template form and mapping view.
 * @param {TranslationService} t Translation service for the current language.
 * @returns {ProductCsvMappingField[]} Product mapping fields in display order.
 */
export function productCsvMappingFields(
  t: TranslationService,
): ProductCsvMappingField[] {
  return [
    { name: 'articleName', label: t.getProductNameText() },
    { name: 'otherNames', label: t.getOtherNamesText() },
    { name: 'primaryArticleIdentifier', label: t.getProductIdentifierText() },
    { name: 'articleCategory', label: t.getArticleCategoryText() },
    { name: 'productionInCanada', label: t.getProducedInCanadaText() },
    { name: 'picture', label: t.getPictureText() },
    { name: 'safeUseInstructions', label: t.getSafeUseInstructionsText() },
    {
      name: 'disassemblingInstructions',
      label: t.getDisassemblingInstructionsText(),
    },
    { name: 'brand', label: t.getBrandText() },
    { name: 'sku', label: t.getSKUText() },
    { name: 'batch', label: t.getLowerBatchRangeText() },
    { name: 'batchUpper', label: t.getUpperBatchRangeText() },
    { name: 'serial', label: t.getLowerSerialRangeText() },
    { name: 'serialUpper', label: t.getUpperSerialRangeText() },
    { name: 'description', label: t.getDescriptionText() },
    { name: 'safetyDataSheet', label: t.getSafetyDataSheetText() },
    { name: 'measureValue', label: t.getWeightText() },
    { name: 'measureUnit', label: t.getMarketplaceWeightUnitText() },
    { name: 'gs1Identifier', label: t.getGs1IdentifierText() },
    { name: 'categoryCode', label: t.getCategoryCodeText() },
    { name: 'codeDescription', label: t.getCodeDescriptionText() },
    { name: 'diameter', label: t.getDiameterText() },
    { name: 'width', label: t.getWidthText() },
    { name: 'height', label: t.getHeightText() },
    { name: 'recyclingInstructions', label: t.getRecyclingInstructionsText() },
    { name: 'type', label: t.getTypeText() },
    { name: 'barcodeIdentifier', label: t.getBarcodeIdentifierText() },
    { name: 'englishShortName', label: t.getEnglishShortNameText() },
    { name: 'frenchShortName', label: t.getFrenchShortNameText() },
    { name: 'ingredientName', label: t.getIngredientNameText() },
    { name: 'mayContain', label: t.getMayContainText() },
    { name: 'doesNotContain', label: t.getDoesNotContainText() },
    { name: 'optionallyDisclosed', label: t.getOptionallyDisclosedText() },
    { name: 'upperConcentration', label: t.getUpperConcentrationText() },
    { name: 'lowerConcentration', label: t.getLowerConcentrationText() },
    { name: 'complianceOfficer', label: t.getComplianceOfficerText() },
    { name: 'wasteHandler', label: t.getWasteHandlerText() },
    { name: 'consumer', label: t.getConsumerText() },
    { name: 'productIdentifier', label: t.getProductIdentifierText() },
    {
      name: 'fullIngredientDisclosure',
      label: t.getFullIngredientDisclosureText(),
    },
    { name: 'isRecyclable', label: t.getIsRecyclable() },
    { name: 'isPackage', label: t.getIsPackageText() },
    { name: 'packagingType', label: t.getPackagingTypeText() },
    { name: 'isPlasticPackaging', label: t.getIsPlasticPackagingText() },
    {
      name: 'recycledContentPercentage',
      label: t.getRecycledContentPercentageText(),
    },
    { name: 'alberta', label: t.getCsvRegionText('Alberta') },
    { name: 'britishColumbia', label: t.getCsvRegionText('British Columbia') },
    { name: 'manitoba', label: t.getCsvRegionText('Manitoba') },
    { name: 'newBrunswick', label: t.getCsvRegionText('New Brunswick') },
    {
      name: 'newfoundlandAndLabrador',
      label: t.getCsvRegionText('Newfoundland and Labrador'),
    },
    {
      name: 'northwestTerritories',
      label: t.getCsvRegionText('Northwest Territories'),
    },
    { name: 'novaScotia', label: t.getCsvRegionText('Nova Scotia') },
    { name: 'nunavut', label: t.getCsvRegionText('Nunavut') },
    { name: 'ontario', label: t.getCsvRegionText('Ontario') },
    {
      name: 'princeEdwardIsland',
      label: t.getCsvRegionText('Prince Edward Island'),
    },
    { name: 'quebec', label: t.getCsvRegionText('Quebec') },
    { name: 'saskatchewan', label: t.getCsvRegionText('Saskatchewan') },
    { name: 'yukon', label: t.getCsvRegionText('Yukon') },
    { name: 'hongKong', label: t.getCsvRegionText('Hong Kong') },
    { name: 'toxic', label: t.getGhsToxicText() },
    { name: 'flammable', label: t.getGhsFlammableText() },
    { name: 'explosive', label: t.getGhsExplosiveText() },
    { name: 'environmentalHazard', label: t.getGhsEnvironmentalHazardText() },
    { name: 'healthHazard', label: t.getGhsHealthHazardText() },
    { name: 'harmful', label: t.getGhsHarmfulText() },
    { name: 'corrosive', label: t.getGhsCorrosiveText() },
    { name: 'oxidizing', label: t.getGhsOxidizingText() },
    { name: 'componentParentSku', label: t.getComponentParentSkuText() },
    { name: 'componentSku', label: t.getComponentSkuText() },
    { name: 'componentName', label: t.getComponentNameText() },
    { name: 'componentGroupName', label: t.getComponentGroupNameText() },
    { name: 'componentQuantity', label: t.getComponentQuantityText() },
    { name: 'componentSupplier', label: t.getComponentSupplierText() },
    { name: 'napcsCode', label: t.getNapcsCodeText() },
    { name: 'productWeightKg', label: t.getProductWeightKgText() },
    { name: 'sourceType', label: t.getSourceTypeText() },
    { name: 'isResidentialWaste', label: t.getIsResidentialWasteText() },
    { name: 'isIciWaste', label: t.getIsIciWasteText() },
    { name: 'isCdrWaste', label: t.getIsCdrWasteText() },
    { name: 'supplier', label: t.getSupplierText() },
    { name: 'category', label: t.getCategoryText() },
    { name: 'subcategory', label: t.getSubcategoryText() },
    { name: 'calculationMethod', label: t.getCalculationMethodText() },
    {
      name: 'calculationMethodOther',
      label: t.getCalculationMethodOtherText(),
    },
    { name: 'reportingDate', label: t.getReportingDateText() },
    { name: 'tags', label: t.getTagsText() },
    { name: 'materialType', label: t.getMaterialTypeText() },
    { name: 'marketplaceCategory', label: t.getMarketplaceCategoryLabelText() },
    {
      name: 'marketplaceSubcategory',
      label: t.getMarketplaceSubcategoryLabelText(),
    },
    { name: 'requestUnit', label: t.getMarketplaceRequestUnitText() },
    { name: 'priceAmount', label: t.getMarketplacePriceText() },
    { name: 'priceCurrency', label: t.getMarketplaceCurrencyText() },
    { name: 'condition', label: t.getMarketplaceConditionText() },
    {
      name: 'droneFlightTimeMinutes',
      label: t.getMarketplaceFlightTimeMinutesText(),
    },
    { name: 'droneType', label: t.getMarketplaceDroneTypeText() },
    {
      name: 'droneVideoResolution',
      label: t.getMarketplaceVideoResolutionText(),
    },
    { name: 'droneSensorSize', label: t.getMarketplaceSensorSizeInchesText() },
    { name: 'droneFpv', label: t.getMarketplaceFpvText() },
    { name: 'allowRequests', label: t.getAllowRequestsText() },
    { name: 'isMarketplacePrivate', label: t.getKeepProductPrivateText() },
  ]
}
